import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { AngleRightIcon, LonaIcons } from "@tento-ui/ui/icons";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import { Typography } from "@tento-ui/ui/typography";
import { Divider } from "@tento-ui/components/divider";
import { PipelineDebug } from "./chat-types";

@component({ name: "std-chat-debug-section" })
export class ChatDebugSection extends LonaWebComponent {
  private $header: HTMLElement = this.$("header");
  private $headerText: HTMLElement = this.$("header-text");
  private $icon: HTMLElement = this.$("icon");
  private $body: HTMLElement = this.$("body");
  private expanded: boolean = true;

  constructor() {
    super();
    this.$icon.appendChild(LonaIcons.$svg(AngleRightIcon));
  }

  connectedCallback() {
    super.connectedCallback();
    GESTURE_MANAGER.addPointerEvent(this.$header, {
      onClick: () => this.setExpanded(!this.expanded),
    });
  }

  bind(props: ChatDebugSection.Props): this {
    this.$headerText.textContent = props.title;
    this.setExpanded(props.expanded ?? true);
    return this;
  }

  private setExpanded(v: boolean): void {
    this.expanded = v;
    this.$icon.classList.toggle("expanded", v);
    this.$body.classList.toggle("collapsed", !v);
    this.$header.setAttribute("aria-expanded", String(v));
  }

  static $styles = [
    Typography.$style,
    css`
      :host {
        display: block;
      }

      #header {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        user-select: none;
        padding: 4px 0;
      }

      #header:focus-visible {
        outline: 2px solid var(--blue);
        outline-offset: 2px;
        border-radius: 4px;
      }

      #icon {
        width: 14px;
        height: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: transform 0.3s ease;
      }

      #icon.expanded {
        transform: rotate(90deg);
      }

      #icon svg {
        width: 14px;
        height: 14px;
        fill: var(--text-sec);
      }

      #header-text {
        --font-size: var(--h7-size);
        --margin-top: 0px;
        --margin-bottom: 0px;
        color: var(--text-sec);
        flex: 1;
      }

      #body {
        margin-top: 8px;
      }

      #body.collapsed {
        display: none;
      }
    `,
  ];

  static $html = html`
    <div id="header" role="button" tabindex="0" aria-expanded="true">
      <div id="icon"></div>
      <h2 id="header-text"></h2>
    </div>
    <div id="body"><slot></slot></div>
  `;
}

export namespace ChatDebugSection {
  export type Props = {
    title: string;
    expanded?: boolean;
  };
}

@component({ name: "std-chat-debug-view" })
export class ChatDebugView extends LonaWebComponent {
  private $content: HTMLElement = this.$("content");
  private entries: ChatDebugView.Entry[] = [];

  /**
   * Replace the current entries and re-render from scratch.
   * Pass `highlightedMessageId` to scroll/highlight that entry.
   */
  bind(props: ChatDebugView.Props): this {
    this.entries = props.entries ?? [];
    this.rerender();
    if (props.highlightedMessageId != null) {
      this.focusMessage(props.highlightedMessageId);
    }
    return this;
  }

  /** Append one entry (newest at the top). */
  addEntry(entry: ChatDebugView.Entry): void {
    this.entries.unshift(entry);
    const $el = ChatDebugView.renderEntry(entry, this.entries.length);
    this.$content.prepend($el);
  }

  /** Scroll and visually highlight the entry for the given messageId. */
  focusMessage(messageId: string): void {
    const $target = this.$content.querySelector<HTMLElement>(
      `.debug-entry[data-message-id="${CSS.escape(messageId)}"]`,
    );
    if (!$target) return;
    // Scope scrolling to `#content`. `scrollIntoView` would walk up the
    // DOM and scroll the outer `<lona-sidebar>` (overflow-y: scroll) too.
    const containerRect = this.$content.getBoundingClientRect();
    const targetRect = $target.getBoundingClientRect();
    const delta = targetRect.top - containerRect.top;
    this.$content.scrollBy({ top: delta, behavior: "smooth" });
    for (const $e of this.$content.querySelectorAll<HTMLElement>(
      ".debug-entry.highlighted",
    )) {
      $e.classList.remove("highlighted");
    }

    $target.classList.add("highlighted");
  }

  clear(): void {
    this.entries = [];
    while (this.$content.firstChild)
      this.$content.removeChild(this.$content.firstChild);
  }

  private rerender(): void {
    while (this.$content.firstChild)
      this.$content.removeChild(this.$content.firstChild);
    // entries[0] is newest, render in reverse so newest renders at top
    // (same as prepend behavior in addEntry).
    for (let i = 0; i < this.entries.length; i++) {
      const entry = this.entries[i];
      this.$content.appendChild(
        ChatDebugView.renderEntry(entry, this.entries.length - i),
      );
    }
  }

  static $styles = [
    Typography.$style,
    Divider.$hr,
    LonaModalHeader.$style,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
        min-width: 0;
        overflow: hidden;
      }

      #title {
        --margin-top: 0px;
      }

      #header p {
        --margin-top: 0px;
        --margin-bottom: 0px;
      }

      #content {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 12px 16px;
        padding-bottom: 58px;
        padding-bottom: 52px;
      }

      .debug-entry {
        position: relative;
        margin-bottom: 8px;
        padding: 8px;
      }

      .debug-entry.highlighted::before {
        content: "";
        position: absolute;
        inset: 0;
        left: -8px;
        right: -8px;
        background-color: color-mix(in srgb, var(--blue) 10%, transparent);
        border-radius: 8px;
        pointer-events: none;
      }

      pre {
        background: var(--bg-surface);
        border-radius: 8px;
        padding: 12px;
        overflow-x: auto;
        white-space: pre-wrap;
        word-break: break-all;
        max-height: 400px;
        overflow-y: auto;
      }

      hr {
        margin: 0;
      }
    `,
  ];

  static $html = html`
    <std-col id="header" class="lona-modal-header">
      <h1 id="title">Agent Logs</h1>
      <p>Debug logs per message</p>
    </std-col>
    <hr />
    <div id="content"></div>
  `;
}

export namespace ChatDebugView {
  export type Props = {
    entries?: Entry[];
    highlightedMessageId?: Option<string>;
  };

  /**
   * A debug entry is one `PipelineDebug` event paired with the assistant
   * `messageId` that was streaming when it arrived. The debug view uses the
   * messageId to scroll / highlight when a bubble is clicked.
   */
  export type Entry = {
    messageId: string;
    debug: PipelineDebug;
  };

  export function metaTable(rows: Array<[string, string]>): HTMLElement {
    const $table = document.createElement("table");
    const $tbody = document.createElement("tbody");
    for (const [label, value] of rows) {
      const $tr = document.createElement("tr");
      const $th = document.createElement("th");
      $th.textContent = label;
      const $td = document.createElement("td");
      const $code = document.createElement("code");
      $code.textContent = value;
      $td.appendChild($code);
      $tr.append($th, $td);
      $tbody.appendChild($tr);
    }
    $table.appendChild($tbody);
    return $table;
  }

  export function nodesTable(nodes: PipelineDebug["nodes"]): HTMLElement {
    const $table = document.createElement("table");
    const $thead = document.createElement("thead");
    const $headRow = document.createElement("tr");
    for (const label of ["", "Node", "Duration", "Detail"]) {
      const $th = document.createElement("th");
      $th.textContent = label;
      $headRow.appendChild($th);
    }
    $thead.appendChild($headRow);
    $table.appendChild($thead);

    const $tbody = document.createElement("tbody");
    for (const node of nodes) {
      const $tr = document.createElement("tr");
      const $status = document.createElement("td");
      $status.textContent = "✓";
      const $name = document.createElement("td");
      $name.textContent = node.name;
      const $duration = document.createElement("td");
      $duration.textContent = `${node.durationMs}ms`;
      const $detail = document.createElement("td");
      $detail.textContent = node.detail ?? "";
      $tr.append($status, $name, $duration, $detail);
      $tbody.appendChild($tr);
    }
    $table.appendChild($tbody);
    return $table;
  }

  function section(
    title: string,
    body: HTMLElement,
    expanded: boolean = true,
  ): ChatDebugSection {
    const $s = ChatDebugSection.make().bind({ title, expanded });
    $s.style.marginBottom = "12px";
    $s.appendChild(body);
    return $s;
  }

  export function renderEntry(entry: Entry, index: number): HTMLElement {
    const { debug, messageId } = entry;
    const $entry = document.createElement("div");
    $entry.className = "debug-entry";
    $entry.dataset.messageId = messageId;

    $entry.appendChild(Typography.h6(`Request #${index}`));
    $entry.appendChild(
      metaTable([
        ["Session", debug.sessionId.slice(0, 8)],
        ["Turns in history", String(debug.turnCount)],
        ["Knowledge entries", String(debug.knowledgeCount)],
      ]),
    );

    if (debug.systemInstruction) {
      const $si = document.createElement("pre");
      $si.textContent = debug.systemInstruction;
      $entry.appendChild(section("System Instruction", $si, true));
    }

    const $reqPre = document.createElement("pre");
    $reqPre.textContent = JSON.stringify(debug.request, null, 2);
    $entry.appendChild(section("Linearized Request", $reqPre, false));

    $entry.appendChild(
      section("Pipeline Nodes", nodesTable(debug.nodes), true),
    );

    $entry.appendChild(document.createElement("hr"));

    return $entry;
  }
}
